import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * SupplierSettings
 *
 * KC's control panel for the Kin's suppliers.
 *
 * Sections:
 *   - What is a vhub? — a short explanation.
 *   - Vhubs — one accordion per chain. Inside: the vhub location
 *     dropdown (nearest 5, by distance), the master segment toggle, the
 *     wrapped segment chips, and a per-vhub Save.
 *   - Producers and hubs — a list, each with an enable checkbox and a
 *     per-vendor Save.
 *   - Delivery — the three modes (Kin pickup, external pickup, supplier
 *     delivery), with the member and pickup dropdowns, and its own Save.
 *
 * Every section saves independently, so a change to one is not held up
 * by an unsaved change elsewhere.
 */
export default function SupplierSettings({ kinId }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState(null);
  const [members, setMembers] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [availableProducers, setAvailableProducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [deliveryExpanded, setDeliveryExpanded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addSearch, setAddSearch] = useState('');

  // Per-vendor local state: { enabled, overrides }.
  const [vendorState, setVendorState] = useState({});

  // Delivery local state.
  const [deliveryState, setDeliveryState] = useState({
    kinPickup: { enabled: false, memberId: null },
    externalPickup: { enabled: false, pickupId: null },
    supplierDelivery: { enabled: true },
  });

  useEffect(() => {
    if (kinId) loadAll();
  }, [kinId]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [settingsRes, suppliersRes, supplierSettingsRes, membersRes, pickupsRes] =
        await Promise.all([
          fetch(`/api/kins/${kinId}/settings`, { headers }),
          fetch(`/api/member-drafts/my/${kinId}/suppliers`, { headers }),
          fetch(`/api/kins/${kinId}/supplier-settings`, { headers }),
          fetch(`/api/kins/${kinId}/members`, { headers }),
          fetch(`/api/kins/${kinId}/nearby-pickups`, { headers }),
        ]);

      let settingsData = null;
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        settingsData = data.settings;
        setSettings(data.settings);
      }

      let suppliersData = { suppliers: [] };
      if (suppliersRes.ok) {
        suppliersData = await suppliersRes.json();
        setSuppliers(suppliersData.suppliers || []);
      }

      let supplierSettingsData = null;
      if (supplierSettingsRes.ok) {
        supplierSettingsData = await supplierSettingsRes.json();
        setStores(supplierSettingsData.stores || null);
      }

      if (membersRes.ok) {
        const data = await membersRes.json();
        setMembers(data.members || []);
      }
      if (pickupsRes.ok) {
        const data = await pickupsRes.json();
        setPickups(data.pickups || []);
      }

      // Initialise vendor state.
      const enabled = settingsData?.vendorsEnabled || ['vhub:coles', 'vhub:woolworths', 'vhub:aldi'];
      const overrides = settingsData?.categoryOverrides || {};
      const vs = {};
      for (const vendorId of ['vhub:coles', 'vhub:woolworths', 'vhub:aldi']) {
        vs[vendorId] = { enabled: enabled.includes(vendorId), overrides: { ...overrides } };
      }
      for (const s of suppliersData.suppliers || []) {
        if (s.type === 'producer' || s.type === 'hub') {
          vs[s.id] = { enabled: enabled.includes(s.id), overrides: {} };
        }
      }
      setVendorState(vs);

      // Initialise delivery state.
      const d = supplierSettingsData?.delivery;
      if (d) {
        setDeliveryState({
          kinPickup: { enabled: !!d.kinPickup?.enabled, memberId: d.kinPickup?.memberId || null },
          externalPickup: { enabled: !!d.externalPickup?.enabled, pickupId: d.externalPickup?.pickupId || null },
          supplierDelivery: { enabled: d.supplierDelivery?.enabled !== false },
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableProducers = async (q = '') => {
    try {
      const token = localStorage.getItem('token');
      const url = `/api/kins/${kinId}/available-producers${q ? `?q=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAvailableProducers(data.availableProducers || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (showAdd) loadAvailableProducers();
  }, [showAdd]);

  // --- Vendor state updates ---

  const setVendorEnabled = (vendorId, enabled) => {
    setVendorState((prev) => ({ ...prev, [vendorId]: { ...prev[vendorId], enabled } }));
  };

  const setCategoryOverride = (vendorId, supplierKey, value) => {
    setVendorState((prev) => ({
      ...prev,
      [vendorId]: {
        ...prev[vendorId],
        overrides: { ...prev[vendorId].overrides, [supplierKey]: value },
      },
    }));
  };

  const setAllCategories = (vendorId, categoryKeys, enabled) => {
    setVendorState((prev) => {
      const next = { ...prev[vendorId].overrides };
      for (const key of categoryKeys) next[key] = enabled;
      return { ...prev, [vendorId]: { ...prev[vendorId], overrides: next } };
    });
  };

  // --- Saves ---

  const mergeVendorEnabled = (vendorId, isEnabled) => {
    const current = new Set(settings?.vendorsEnabled || []);
    if (isEnabled) current.add(vendorId);
    else current.delete(vendorId);
    return [...current];
  };

  const saveVendor = async (vendorId) => {
    setSavingKey(vendorId);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const vs = vendorState[vendorId];
      if (!vs) return;

      const res = await fetch(`/api/kins/${kinId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vendorsEnabled: mergeVendorEnabled(vendorId, vs.enabled),
          categoryOverrides: vs.overrides,
          pickupEnabled: settings?.pickupEnabled,
          externalDeliveryOK: settings?.externalDeliveryOK,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }

      setSavedAt((prev) => ({ ...prev, [vendorId]: new Date() }));
      setSettings((prev) => ({
        ...prev,
        vendorsEnabled: mergeVendorEnabled(vendorId, vs.enabled),
        categoryOverrides: vs.overrides,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  const saveDelivery = async () => {
    setSavingKey('delivery');
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kins/${kinId}/supplier-settings/delivery`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(deliveryState),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save delivery');
      }
      setSavedAt((prev) => ({ ...prev, delivery: new Date() }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  const changeStore = async (chain, storeId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kins/${kinId}/supplier-settings/stores`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ chain, storeId }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to change store');
      }
      const data = await res.json();
      setStores(data.stores);
    } catch (err) {
      setError(err.message);
    }
  };

  // --- Render helpers ---

  const CATEGORY_LABELS = {
    'meat-seafood': 'Meat & Seafood',
    'fruit-vegetables': 'Fruit & Veg',
    'dairy-eggs-fridge': 'Dairy & Eggs',
    'bakery': 'Bakery',
    'deli': 'Deli',
    'pantry': 'Pantry',
    'drinks': 'Drinks',
    'frozen': 'Frozen',
    'household': 'Household',
    'health-beauty': 'Health & Beauty',
    'baby': 'Baby',
    'pet': 'Pet',
    'liquor': 'Liquor',
  };
  const categoryLabel = (key) => {
    const parts = key.split(':');
    const cat = parts[2] || parts[1];
    return CATEGORY_LABELS[cat] || cat;
  };
  const chainLabel = (c) => c.charAt(0).toUpperCase() + c.slice(1);
  const vhubChains = ['coles', 'woolworths', 'aldi'];

  if (loading) return <div className="text-gray-500">Loading suppliers...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">🏪 Suppliers</h3>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* --- What is a vhub? --- */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">What is a vhub?</div>
        <p>
          A vhub is a virtual supermarket — a storefront within OP where you browse a
          supermarket's products and prices, build a shopping list, and use the
          calculation engine to see what your Kin would save by consolidating the order.
          Vhubs give your Kin a full range of products from day one, while local
          producers join over time. Each vhub is split into segments (pantry, dairy,
          and so on) so members can browse and compare the areas they care about.
        </p>
      </div>

      {/* --- Vhubs --- */}
      <div>
        <h4 className="font-medium text-gray-700 mb-2">Vhubs</h4>
        <div className="space-y-2">
          {vhubChains.map((chain) => {
            const vendorId = `vhub:${chain}`;
            const vs = vendorState[vendorId];
            if (!vs) return null;
            const st = stores?.[chain];
            const isExpanded = expandedVendor === vendorId;
            const chainSuppliers = suppliers.filter(
              (s) => s.type === 'vhub' && s.vendorId === vendorId
            );
            const categoryKeys = chainSuppliers.map((s) => s.id);
            const enabledCount = categoryKeys.filter((k) => vs.overrides[k] !== false).length;

            return (
              <div key={chain} className="border rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setExpandedVendor(isExpanded ? null : vendorId)}
                  className="w-full flex items-center justify-between p-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">{isExpanded ? '▼' : '▶'}</span>
                    <div>
                      <div className="font-medium">{chainLabel(chain)}</div>
                      <div className="text-sm text-gray-500">
                        {st?.selected ? st.selected.displayName : 'No store selected'}
                        {st?.isDefault && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Nearest</span>}
                      </div>
                      <div className="text-xs text-gray-400">
                        {enabledCount} of {categoryKeys.length} segments enabled
                      </div>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t p-3 space-y-4">
                    {/* Vhub location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vhub location
                      </label>
                      <select
                        value={st?.selected?._id || ''}
                        onChange={(e) => changeStore(chain, e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      >
                        {(st?.alternatives || []).map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.displayName} — {s.distanceKm} km
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Segments */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={categoryKeys.length > 0 && enabledCount === categoryKeys.length}
                            onChange={(e) => setAllCategories(vendorId, categoryKeys, e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-medium">All segments</span>
                        </label>
                        <div className="flex items-center gap-2">
                          {savedAt[vendorId] && <span className="text-xs text-green-600">Saved</span>}
                          <button
                            onClick={() => saveVendor(vendorId)}
                            disabled={savingKey === vendorId}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 text-sm"
                          >
                            {savingKey === vendorId ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {categoryKeys.map((key) => {
                          const enabled = vs.overrides[key] !== false;
                          return (
                            <label
                              key={key}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer text-sm ${
                                enabled
                                  ? 'bg-green-50 border-green-300 text-green-800'
                                  : 'bg-gray-50 border-gray-300 text-gray-500'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => setCategoryOverride(vendorId, key, e.target.checked)}
                                className="w-3 h-3"
                              />
                              {categoryLabel(key)}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Vendor enabled */}
                    <label className="flex items-center gap-2 cursor-pointer pt-2 border-t">
                      <input
                        type="checkbox"
                        checked={vs.enabled}
                        onChange={(e) => setVendorEnabled(vendorId, e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">This vhub is available to Kin members</span>
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* --- Producers and hubs --- */}
      {['producer', 'hub'].map((type) => {
        const list = suppliers.filter((s) => s.type === type);
        if (list.length === 0) return null;
        const label = type === 'producer' ? '🚜 Producers' : '🏪 Hubs';
        return (
          <div key={type}>
            <h4 className="font-medium text-gray-700 mb-2">{label}</h4>
            <div className="space-y-2">
              {list.map((s) => {
                const vs = vendorState[s.id] || { enabled: false };
                return (
                  <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                    <label className="flex items-center gap-3 cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        checked={vs.enabled}
                        onChange={(e) => setVendorEnabled(s.id, e.target.checked)}
                        className="w-4 h-4"
                      />
                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-sm text-gray-500">
                          {s.productCount} product{s.productCount !== 1 ? 's' : ''} available
                        </div>
                      </div>
                    </label>
                    <div className="flex items-center gap-2">
                      {savedAt[s.id] && <span className="text-xs text-green-600">Saved</span>}
                      <button
                        onClick={() => saveVendor(s.id)}
                        disabled={savingKey === s.id}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 text-sm"
                      >
                        {savingKey === s.id ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* --- Add a supplier --- */}
      <div className="pt-2 border-t">
        {!showAdd ? (
          <button
            onClick={() => setShowAdd(true)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            + Add a supplier
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Add a supplier</h4>
              <button
                onClick={() => { setShowAdd(false); setAddSearch(''); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
            <input
              type="text"
              value={addSearch}
              onChange={(e) => { setAddSearch(e.target.value); loadAvailableProducers(e.target.value); }}
              placeholder="Search by name..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
            <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2">
              {availableProducers.length === 0 ? (
                <div className="text-sm text-gray-500 p-3">No additional suppliers found.</div>
              ) : (
                availableProducers.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border border-gray-100">
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-sm text-gray-500">
                        {p.productCount} product{p.productCount !== 1 ? 's' : ''}
                        {p.distanceKm != null && ` · ${p.distanceKm < 1 ? '<1 km' : `${Math.round(p.distanceKm)} km`}`}
                      </div>
                    </div>
                    <button
                      onClick={() => { setVendorEnabled(p.id, true); setShowAdd(false); setAddSearch(''); }}
                      className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Add
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- Delivery --- */}
      <div className="border rounded-lg bg-white">
        <button
          type="button"
          onClick={() => setDeliveryExpanded(!deliveryExpanded)}
          className="w-full flex items-center justify-between p-3 text-left"
        >
          <div className="flex items-center gap-3">
            <span className="text-gray-500">{deliveryExpanded ? '▼' : '▶'}</span>
            <div>
              <div className="font-medium">🚚 Delivery</div>
              <div className="text-xs text-gray-500">
                {[deliveryState.kinPickup.enabled, deliveryState.externalPickup.enabled, deliveryState.supplierDelivery.enabled].filter(Boolean).length} of 3 options enabled
              </div>
            </div>
          </div>
        </button>

        {deliveryExpanded && (
          <div className="border-t p-3 space-y-4">
            {/* Kin pickup */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliveryState.kinPickup.enabled}
                  onChange={(e) => setDeliveryState({
                    ...deliveryState,
                    kinPickup: { ...deliveryState.kinPickup, enabled: e.target.checked },
                  })}
                  className="w-4 h-4"
                />
                <span className="font-medium">Kin does its own Pickups</span>
              </label>
              {deliveryState.kinPickup.enabled && (
                <div className="ml-6">
                  <label className="block text-sm text-gray-600 mb-1">Pickup member</label>
                  <select
                    value={deliveryState.kinPickup.memberId || ''}
                    onChange={(e) => setDeliveryState({
                      ...deliveryState,
                      kinPickup: { ...deliveryState.kinPickup, memberId: e.target.value || null },
                    })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">— Select a member —</option>
                    {members.map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}{m.memberNumber != null ? ` (#${m.memberNumber})` : ''}
                        {m.role === 'leader' ? ' — Leader' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* External pickup */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliveryState.externalPickup.enabled}
                  onChange={(e) => setDeliveryState({
                    ...deliveryState,
                    externalPickup: { ...deliveryState.externalPickup, enabled: e.target.checked },
                  })}
                  className="w-4 h-4"
                />
                <span className="font-medium">Kin has a preferred external Pickup</span>
              </label>
              {deliveryState.externalPickup.enabled && (
                <div className="ml-6">
                  <label className="block text-sm text-gray-600 mb-1">External Pickup</label>
                  <select
                    value={deliveryState.externalPickup.pickupId || ''}
                    onChange={(e) => setDeliveryState({
                      ...deliveryState,
                      externalPickup: { ...deliveryState.externalPickup, pickupId: e.target.value || null },
                    })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">— Select a pickup —</option>
                    {pickups.map((p) => (
                      <option key={p._id} value={p._id}>{p.displayName}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Supplier delivery */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliveryState.supplierDelivery.enabled}
                  onChange={(e) => setDeliveryState({
                    ...deliveryState,
                    supplierDelivery: { enabled: e.target.checked },
                  })}
                  className="w-4 h-4"
                />
                <span className="font-medium">Kin uses the supplier's delivery service</span>
              </label>
              <p className="ml-6 text-sm text-gray-500 mt-1">
                Suppliers deliver directly to the Kin.
              </p>
            </div>

            {/* Save */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              {savedAt.delivery && <span className="text-xs text-green-600">Saved</span>}
              <button
                onClick={saveDelivery}
                disabled={savingKey === 'delivery'}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm"
              >
                {savingKey === 'delivery' ? 'Saving...' : 'Save delivery'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
